import axios, { InternalAxiosRequestConfig } from 'axios';

// Import mock data handlers for fallback
import { getMockProductsResponse, getMockCategoriesResponse } from './mockData';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://api.madanglers.co.za/v1').replace(/\/+$/, '');
const API_TIMEOUT = Number(process.env.REACT_APP_API_TIMEOUT || 30000);

// Create axios instance with defaults
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add interceptor to include auth token in requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error: any) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem('authToken');
    }

    return Promise.reject(error);
  }
);

// Authentication APIs
export const authAPI = {
  login: async (username: string, password: string) => {
    try {
      const response = await apiClient.post('/auth/login', { username, password });
      if (response.data.token) {
        localStorage.setItem('authToken', response.data.token);
      }
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },
  
  logout: async () => {
    try {
      await apiClient.post('/auth/logout');
      localStorage.removeItem('authToken');
    } catch (error) {
      console.error('Logout error:', error);
      // Remove token anyway to ensure user is logged out on client side
      localStorage.removeItem('authToken');
      throw error;
    }
  },
  
  checkStatus: async () => {
    try {
      const response = await apiClient.get('/auth/status');
      return response.data;
    } catch (error) {
      localStorage.removeItem('authToken');
      throw error;
    }
  }
};

// Products APIs
export const productsAPI = {
  getAll: async (params?: any) => {
    try {
      const response = await apiClient.get('/products', { params });
      return response;
    } catch (error) {
      console.error('Error fetching products:', error);
      console.warn('Using mock product data as fallback');
      // Return mock data as fallback
      return getMockProductsResponse();
    }
  },
  
  getById: async (id: string) => {
    try {
      const response = await apiClient.get(`/products/${id}`);
      return response;
    } catch (error) {
      console.error(`Error fetching product ${id}:`, error);
      throw error;
    }
  },
  
  create: async (productData: any) => {
    try {
      const response = await apiClient.post('/products', productData);
      return response;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },
  
  update: async (id: string, productData: any) => {
    try {
      const response = await apiClient.put(`/products/${id}`, productData);
      return response;
    } catch (error) {
      console.error(`Error updating product ${id}:`, error);
      throw error;
    }
  },
  
  delete: async (id: string) => {
    try {
      const response = await apiClient.delete(`/products/${id}`);
      return response;
    } catch (error) {
      console.error(`Error deleting product ${id}:`, error);
      throw error;
    }
  }
};

// Categories APIs
export const categoriesAPI = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/categories');
      return response;
    } catch (error) {
      console.error('Error fetching categories:', error);
      console.warn('Using mock category data as fallback');
      // Return mock data as fallback
      return getMockCategoriesResponse();
    }
  },
  
  getById: async (id: string) => {
    try {
      const response = await apiClient.get(`/categories/${id}`);
      return response;
    } catch (error) {
      console.error(`Error fetching category ${id}:`, error);
      throw error;
    }
  },
  
  create: async (categoryData: any) => {
    try {
      const response = await apiClient.post('/categories', categoryData);
      return response;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  },
  
  update: async (id: string, categoryData: any) => {
    try {
      const response = await apiClient.put(`/categories/${id}`, categoryData);
      return response;
    } catch (error) {
      console.error(`Error updating category ${id}:`, error);
      throw error;
    }
  },
  
  delete: async (id: string) => {
    try {
      const response = await apiClient.delete(`/categories/${id}`);
      return response;
    } catch (error) {
      console.error(`Error deleting category ${id}:`, error);
      throw error;
    }
  }
};

// Image upload API
export const uploadAPI = {
  uploadImage: async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/uploads/images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  },
  
  deleteImage: async (filename: string) => {
    try {
      const response = await apiClient.delete(`/uploads/images/${filename}`);
      return response;
    } catch (error) {
      console.error(`Error deleting image ${filename}:`, error);
      throw error;
    }
  }
};

const api = {
  auth: authAPI,
  products: productsAPI,
  categories: categoriesAPI,
  uploads: uploadAPI
};

export default api;